<!--
  管理端 · 单船计算参数配置
  --------------------------------------------------------------------
  入口：船舶列表卡片"参数配置"按钮（路由 /ship_list/ship_calc_param?shipId=xxx）
  布局：上半部分全局默认参数只读预览 + 调整抽屉；下半部分该船参数独立编辑
  数据：svc_ship_config（每船快照）+ svc_config（全局默认，兜底/预览）
  注：SIM 卡号（sim.number）为船舶级信息——无默认值概念，默认参数区不展示，
  仅"该船参数"区按船录入（可空），用户端船舶信息页只读展示
-->
<template>
  <div class="ship-cp">
    <header class="ship-cp__hero">
      <div class="hero__left">
        <el-button
          class="hero__back"
          text
          circle
          size="large"
          :icon="ArrowLeft"
          @click="goBack"
        />
        <div class="hero__icon">
          <el-icon :size="22"><Tools /></el-icon>
        </div>
        <div class="hero__text">
          <h1 class="hero__title">
            {{ currentShipName }}
            <span class="hero__title-divider">·</span>
            <span class="hero__title-sub">参数配置</span>
          </h1>
          <p class="hero__subtitle">
            KPI 计算参数管理中心 ·
            <span class="hero__time">{{ formattedNow }}</span>
          </p>
        </div>
      </div>
      <div class="hero__actions">
        <el-tooltip content="从服务端重新拉拉" placement="top">
          <el-button
            :icon="Refresh"
            round
            :loading="loading"
            @click="reloadAll"
          >
            刷新
          </el-button>
        </el-tooltip>
      </div>
    </header>

    <el-empty
      v-if="!shipId"
      class="ship-cp__empty"
      description="请从船舶列表选择船舶"
    >
      <el-button type="primary" plain round @click="goBack">
        返回船舶列表
      </el-button>
    </el-empty>

    <template v-else>
      <!-- 默认参数（只读预览） -->
      <section class="ship-cp__defaults">
        <div class="section-head">
          <div class="section-head__title">
            <el-icon :size="14"><Setting /></el-icon>
            <span>全局默认参数</span>
            <el-tag
              size="small"
              type="info"
              effect="plain"
              class="section-head__tag"
            >
              仅作参考
            </el-tag>
          </div>
          <el-button
            type="primary"
            plain
            round
            :icon="Edit"
            :disabled="visibleDefaults.length === 0"
            @click="openDefaultsManage"
          >
            调整默认值
          </el-button>
        </div>
        <el-empty
          v-if="visibleDefaults.length === 0"
          description="默认参数未初始化"
          class="section-empty"
        />
        <div v-else class="defaults-table">
          <div class="defaults-table__head">
            <span>参数名</span>
            <span>当前默认值</span>
            <span>单位</span>
          </div>
          <div
            v-for="item in visibleDefaults"
            :key="item.configKey"
            class="defaults-table__row"
            :class="`is-${categoryOf(item).key}`"
          >
            <span class="defaults-table__name">
              <span
                class="defaults-table__chip"
                :class="`is-${categoryOf(item).key}`"
              >
                <el-icon :size="11">
                  <component :is="categoryOf(item).icon" />
                </el-icon>
              </span>
              {{ item.cname }}
            </span>
            <span class="defaults-table__value">{{ item.configValue }}</span>
            <span class="defaults-table__unit">{{ item.unit || "—" }}</span>
          </div>
          <div class="defaults-table__foot">
            <el-icon :size="12"><InfoFilled /></el-icon>
            默认值仅在新建船舶时复制为该船初始快照；修改不影响已建船舶（已有船在下方独立维护）。单价默认按人民币计价，其他币种的默认值按当前汇率自动换算
          </div>
        </div>
      </section>

      <!-- 该船参数（编辑区） -->
      <section class="ship-cp__ship">
        <div class="section-head">
          <div class="section-head__title">
            <el-icon :size="14"><Ship /></el-icon>
            <span>该船参数</span>
            <span v-if="currentShip" class="status-chip">
              上次修改 ·
              {{
                currentShip.updatetime
                  ? relativeTime(currentShip.updatetime)
                  : "尚未修改"
              }}
              <span class="status-dot"></span>
              已改 {{ customizedCount }} 项
            </span>
          </div>
          <div class="section-head__actions">
            <el-popconfirm
              title="确定将该船参数恢复为默认值？单价按本船币种取预设（外币无预设时保留当前值），货币与能效K保持不变，SIM卡号将被清空"
              confirm-button-text="恢复"
              cancel-button-text="取消"
              @confirm="resetShip"
            >
              <template #reference>
                <el-button round plain :icon="RefreshLeft" :loading="resetting">
                  恢复默认
                </el-button>
              </template>
            </el-popconfirm>
            <el-button
              type="primary"
              round
              :loading="savingShip"
              :disabled="!shipDirty"
              @click="saveShip"
            >
              保存修改
            </el-button>
          </div>
        </div>

        <section
          v-if="shipsLoading && shipDefaults.length === 0"
          class="section-skeleton"
        >
          <div v-for="i in 4" :key="i" class="skeleton-card">
            <el-skeleton :rows="3" animated />
          </div>
        </section>

        <el-empty
          v-else-if="shipDefaults.length === 0"
          class="section-empty"
          description="默认参数未初始化，请先在「调整默认值」中配置"
        />

        <section v-else class="ship-param-grid">
          <article
            v-for="d in shipDefaults"
            :key="d.configKey"
            class="ship-param-card"
            :class="[
              `ship-param-card--${categoryOf(d).key}`,
              {
                'is-modified':
                  isCustomized(d.configKey) || isEdited(d.configKey),
              },
            ]"
          >
            <div class="ship-param-card__bar"></div>
            <div class="ship-param-card__body">
              <div class="ship-param-card__head">
                <h3 class="ship-param-card__title">{{ d.cname }}</h3>
                <div class="ship-param-card__head-right">
                  <!-- 只读参数徽标：船舶级不可修改，仅可在"默认参数"页签改默认值 -->
                  <span
                    v-if="isReadonlyKey(d.configKey)"
                    class="ship-param-card__tag ship-param-card__tag--readonly"
                    title="船舶级只读：值来自系统默认或用户端建船时填写，仅可在「默认参数」页签修改默认值"
                  >
                    <el-icon :size="11"><Lock /></el-icon>
                    只读
                  </span>
                  <span
                    v-else-if="isCustomized(d.configKey)"
                    class="ship-param-card__tag"
                  >
                    <el-icon :size="11"><Edit /></el-icon>
                    已定制
                  </span>
                  <span
                    v-else-if="isEdited(d.configKey)"
                    class="ship-param-card__tag ship-param-card__tag--editing"
                  >
                    <el-icon :size="11"><Aim /></el-icon>
                    待保存
                  </span>
                  <!-- 单参数恢复默认（只读参数除外；货币恢复联动单价，单价按该船币种换算默认） -->
                  <button
                    v-if="!isReadonlyKey(d.configKey)"
                    class="ship-param-card__reset"
                    :disabled="keyResetting === d.configKey"
                    @click="resetSingleKey(d)"
                  >
                    {{ keyResetting === d.configKey ? "恢复中…" : "恢复默认" }}
                  </button>
                </div>
              </div>
              <div class="ship-param-card__compare">
                <div class="ship-compare-col">
                  <div class="ship-compare-col__label">默认值</div>
                  <div class="ship-compare-col__value">
                    {{ shipDefaultDisplay(d).text }}
                  </div>
                  <div class="ship-compare-col__unit">
                    {{ shipDefaultDisplay(d).unit }}
                  </div>
                </div>
                <div class="ship-compare-arrow">
                  <el-icon :size="18"><Right /></el-icon>
                </div>
                <div class="ship-compare-col ship-compare-col--now">
                  <div class="ship-compare-col__label">该船当前值</div>
                  <!-- 货币参数：下拉选择（ISO 4217）；修改货币后请同步核对该船柴油/电价单价 -->
                  <el-select
                    v-if="isCurrencyKey(d.configKey)"
                    v-model="shipEditValues[d.configKey]"
                    :disabled="isReadonlyKey(d.configKey)"
                    size="large"
                    class="ship-compare-input"
                  >
                    <el-option
                      v-for="c in CURRENCY_OPTIONS"
                      :key="c.code"
                      :value="c.code"
                      :label="`${c.cname} ${c.code} ${c.symbol}`"
                    />
                  </el-select>
                  <!-- SIM 卡号：文本输入（默认为空，支持清空，仅字母/数字/连字符） -->
                  <el-input
                    v-else-if="isSimKey(d.configKey)"
                    v-model="shipEditValues[d.configKey]"
                    :disabled="isReadonlyKey(d.configKey)"
                    :maxlength="SIM_MAX_LENGTH"
                    size="large"
                    class="ship-compare-input ship-compare-input--sim"
                    placeholder="未录入"
                    clearable
                  />
                  <el-input-number
                    v-else
                    v-model="shipEditValues[d.configKey]"
                    :min="0.0001"
                    :step="stepOf(d)"
                    :precision="precisionOf(d)"
                    :controls="false"
                    :disabled="isReadonlyKey(d.configKey)"
                    size="large"
                    class="ship-compare-input"
                    placeholder="正数"
                  />
                  <div class="ship-compare-col__unit">{{ shipUnitOf(d) }}</div>
                </div>
              </div>
              <p class="ship-param-card__desc" :title="d.description">
                {{ d.description }}
              </p>
              <div class="ship-param-card__footer">
                <code class="ship-param-card__key">{{ d.configKey }}</code>
              </div>
            </div>
          </article>
        </section>
      </section>
    </template>

    <!-- 默认值管理 Drawer -->
    <el-drawer
      v-model="defaultsDrawerVisible"
      direction="rtl"
      size="540"
      :show-close="false"
      :with-header="false"
      class="defaults-drawer"
    >
      <template v-if="defaultsEditingItem">
        <header class="drawer-header">
          <div class="drawer-header__title">
            <span
              class="drawer-header__chip"
              :class="`is-${categoryOf(defaultsEditingItem).key}`"
            >
              <el-icon :size="13">
                <component :is="categoryOf(defaultsEditingItem).icon" />
              </el-icon>
              {{ categoryOf(defaultsEditingItem).label }}
            </span>
            <h2>{{ defaultsEditingItem.cname }}</h2>
          </div>
          <el-button text circle @click="defaultsDrawerVisible = false">
            <el-icon :size="18"><Close /></el-icon>
          </el-button>
        </header>
        <div class="drawer-body">
          <div class="drawer-section">
            <div class="meta-row">
              <span class="meta-row__label">参数键</span>
              <code class="meta-row__value">{{
                defaultsEditingItem.configKey
              }}</code>
            </div>
            <div class="meta-row">
              <span class="meta-row__label">上次修改</span>
              <span class="meta-row__value">
                <el-icon :size="12"><User /></el-icon>
                {{ defaultsEditingItem.updaterId || "系统" }}
                <span class="meta-row__sep">·</span>
                <el-icon :size="12"><Clock /></el-icon>
                {{ formatFullTime(defaultsEditingItem.updatetime) }}
              </span>
            </div>
          </div>
          <div class="drawer-section">
            <div class="drawer-section__label">设置新值</div>
            <el-select
              v-if="
                defaultsEditingItem &&
                isCurrencyKey(defaultsEditingItem.configKey)
              "
              v-model="defaultEditValue"
              size="large"
              class="drawer-input"
            >
              <el-option
                v-for="c in CURRENCY_OPTIONS"
                :key="c.code"
                :value="c.code"
                :label="`${c.cname} ${c.code} ${c.symbol}`"
              />
            </el-select>
            <el-input-number
              v-else
              v-model="defaultEditValue"
              :min="0.0001"
              :step="stepOf(defaultsEditingItem!)"
              :precision="precisionOf(defaultsEditingItem!)"
              :controls="true"
              size="large"
              class="drawer-input"
              placeholder="请输入正数"
            />
            <div v-if="defaultValidationError" class="drawer-error">
              <el-icon :size="14"><WarningFilled /></el-icon>
              {{ defaultValidationError }}
            </div>
          </div>
          <div class="drawer-section drawer-nav">
            <el-button
              :disabled="defaultsCurrentIndex <= 0"
              @click="prevDefault"
            >
              <el-icon><ArrowLeft /></el-icon>
              上一个
            </el-button>
            <span class="drawer-nav__pos">
              {{ defaultsCurrentIndex + 1 }}
              <span class="drawer-nav__divider">/</span>
              {{ visibleDefaults.length }}
            </span>
            <el-button
              :disabled="defaultsCurrentIndex >= visibleDefaults.length - 1"
              @click="nextDefault"
            >
              下一个
              <el-icon><ArrowRight /></el-icon>
            </el-button>
          </div>
        </div>
        <footer class="drawer-footer">
          <el-button @click="defaultsDrawerVisible = false">取消</el-button>
          <el-button
            type="primary"
            :loading="defaultsSaving"
            :disabled="defaultValidationError !== ''"
            @click="confirmDefaultEdit"
          >
            保存修改
          </el-button>
        </footer>
      </template>
    </el-drawer>
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import type { Component } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ElMessage, ElMessageBox } from "element-plus";

// 兼容 ESLint 缓存可能引用的历史变量名（page single-ship 单船模式下不再使用）
// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-undef
const selectedShipId = ref<string>("");
import {
  Aim,
  ArrowLeft,
  ArrowRight,
  Clock,
  Close,
  Coin,
  Edit,
  InfoFilled,
  Lock,
  Refresh,
  RefreshLeft,
  Right,
  Setting,
  Ship,
  Sunny,
  Tools,
  User,
  WarningFilled,
} from "@element-plus/icons-vue";
import {
  getSysConfigListApi,
  updateSysConfigApi,
  getShipConfigListApi,
  updateShipConfigApi,
  resetShipConfigApi,
  resetShipConfigKeyApi,
  SysConfigItem,
  ShipConfigShip,
} from "@/api/sysConfigApi";
import { getCurrenciesApi, getEffectiveRatesApi } from "@/api/exchangeRateApi";

type CategoryKey = "carbon" | "price" | "other";

const CATEGORY_META: Record<CategoryKey, { label: string; icon: Component }> = {
  carbon: { label: "碳排放因子", icon: Sunny },
  price: { label: "能源单价", icon: Coin },
  other: { label: "其它", icon: Setting },
};

function categoryOf(item: SysConfigItem): {
  key: CategoryKey;
  label: string;
  icon: Component;
} {
  const key = item.configKey.toLowerCase();
  const unit = (item.unit ?? "").toLowerCase();
  if (key.includes("carbon") || unit.includes("co")) {
    return { key: "carbon", ...CATEGORY_META.carbon };
  }
  if (key.includes("price") || unit.includes("元")) {
    return { key: "price", ...CATEGORY_META.price };
  }
  return { key: "other", ...CATEGORY_META.other };
}

/**
 * 船舶级只读参数（与后端 ShipCalcParamService.READONLY_SHIP_KEYS 保持一致）：
 * 能效K值——值来自系统默认或用户端建船填写，
 * 管理端"船舶参数"页签仅可查看，默认值在"默认参数"页签维护。
 * （原「平均电价」calc.price.avg.electricity 已下线：与用电单价语义重复）
 */
const READONLY_SHIP_KEYS = new Set(["calc.factor.energy.k"]);
function isReadonlyKey(configKey: string): boolean {
  return READONLY_SHIP_KEYS.has(configKey);
}

/** 船舶本位货币参数 key（渲染为下拉选择，非数值） */
const CURRENCY_KEY = "calc.currency";
function isCurrencyKey(configKey: string): boolean {
  return configKey === CURRENCY_KEY;
}

/**
 * SIM 卡号参数 key（船舶级信息，非计算参数）：
 * 字符串、默认为空——"默认参数"区不展示（无默认值概念），
 * "该船参数"区渲染为文本输入（支持清空），用户端只读展示
 */
const SIM_KEY = "sim.number";
/** SIM 卡号最大长度（与后端 ShipCalcParamService.MAX_SIM_NUMBER_LENGTH 一致） */
const SIM_MAX_LENGTH = 32;
function isSimKey(configKey: string): boolean {
  return configKey === SIM_KEY;
}

/** 该船当前本位货币（船舶参数卡单位动态显示用） */
function shipCurrency(): string {
  return String(currentShip.value?.values?.[CURRENCY_KEY] ?? "CNY");
}

/** 单价类参数 key（恢复默认提示文案用） */
function isPriceKey(configKey: string): boolean {
  return (
    configKey === "calc.price.diesel" || configKey === "calc.price.electricity"
  );
}

/** 当前生效汇率（对 CNY），外币船"默认值"列换算显示用 */
const effectiveRates = ref<Record<string, number> | null>(null);
const loadEffectiveRates = async () => {
  const r = await getEffectiveRatesApi();
  if (r) effectiveRates.value = r.rates;
};

/** 汇率(from→to)：对 CNY 交叉；任一外币缺汇率返回 null（调用方提示手动核对） */
const fxOf = (from: string, to: string): number | null => {
  const r = effectiveRates.value;
  if (!r) return null;
  const f = from === "CNY" ? 1 : r[from];
  const t = to === "CNY" ? 1 : r[to];
  if (!f || !t) return null;
  return f / t;
};

/**
 * 船舶页签左列"默认值"（数值+单位成对返回）：
 * 单价类按该船货币显示换算默认——外币默认 = svc_config 人民币默认 × 汇率
 * （与后端 getPricesForCurrency 同口径），单位随数值币种走（如 EUR/L）；
 * 未录汇率的外币或非单价参数显示人民币原值（单位人民币/L）
 */
function shipDefaultDisplay(d: SysConfigItem): { text: string; unit: string } {
  const cur = shipCurrency();
  const fx = cur !== "CNY" ? effectiveRates.value?.[cur] : null;
  if (
    d.configKey === "calc.price.diesel" ||
    d.configKey === "calc.price.electricity"
  ) {
    const suffix = d.configKey === "calc.price.diesel" ? "/L" : "/kWh";
    const v = parseFloat(d.configValue);
    if (fx && Number.isFinite(v)) {
      return { text: (v / fx).toFixed(3), unit: `${cur}${suffix}` };
    }
    return { text: d.configValue, unit: `人民币${suffix}` };
  }
  // SIM 卡号无默认值概念（恒为空），左列显示占位符
  if (isSimKey(d.configKey)) {
    return { text: "—", unit: "—" };
  }
  return { text: d.configValue, unit: d.unit || "—" };
}

/** 船舶参数卡"该船当前值"的单位：单价类按该船货币动态拼接，其余用全局 unit */
function shipUnitOf(d: { configKey: string; unit?: string | null }): string {
  if (d.configKey === "calc.price.diesel") return `${shipCurrency()}/L`;
  if (d.configKey === "calc.price.electricity") return `${shipCurrency()}/kWh`;
  return d.unit || "—";
}

/** 可选币种（接口拉取：已录汇率快照的币种 + CNY 基准；未录任何汇率时仅 CNY） */
const CURRENCY_OPTIONS = ref<{ code: string; symbol: string; cname: string }[]>(
  [{ code: "CNY", symbol: "¥", cname: "人民币" }]
);
const loadCurrencyOptions = async () => {
  const list = await getCurrenciesApi();
  if (list) {
    CURRENCY_OPTIONS.value = [
      { code: "CNY", symbol: "¥", cname: "人民币" },
      ...list.filter((c) => c.hasRate),
    ];
  }
};

const route = useRoute();
const router = useRouter();

/* 路由参数（shipId 从 query 传入）。
 * 注：vue-router 默认复用同名路由的组件实例，watch 不会在参数首次变化时触发，
 * 故 shipId 必须从 route.query 直接初始化（不能依赖 watch 的 immediate 行为）。*/
const shipId = ref<string>(String(route.query.shipId ?? "").trim());
/** setup 是否已完成（避免 watch 在 reloadAll 还未定义时被触发 TDZ） */
const loaded = ref(false);
watch(
  () => route.query.shipId,
  (v) => {
    const id = String(v ?? "").trim();
    shipId.value = id;
    if (id && loaded.value) {
      reloadAll();
    }
  }
);

/* 默认参数（svc_config）：只读预览 + 抽屉编辑 */
const defaults = ref<SysConfigItem[]>([]);
const defaultsDrawerVisible = ref(false);
const defaultsSaving = ref(false);
const defaultsEditingItem = ref<SysConfigItem | null>(null);
const defaultsEditingOriginal = ref<SysConfigItem | null>(null);
const defaultEditValue = ref<number | string>(0);
const defaultsCurrentIndex = ref(0);

const defaultValidationError = computed(() => {
  // 货币参数：非空即可（选项本身受控）
  if (
    defaultsEditingItem.value &&
    isCurrencyKey(defaultsEditingItem.value.configKey)
  ) {
    return defaultEditValue.value ? "" : "请选择货币";
  }
  const v = defaultEditValue.value;
  if (typeof v !== "number" || Number.isNaN(v)) return "请输入正数";
  if (!Number.isFinite(v)) return "不能为无穷大";
  if (v <= 0) return "必须为正数";
  return "";
});

const loading = ref(false);

/**
 * 默认参数区可见列表：过滤 SIM 卡号（船舶级信息，无默认值概念，
 * 后端 /sysConfig/update 同样拒绝修改其默认值，仅"该船参数"区按船录入）
 */
const visibleDefaults = computed<SysConfigItem[]>(() =>
  defaults.value.filter((d) => !isSimKey(d.configKey))
);

const loadDefaults = async () => {
  const resp = await getSysConfigListApi();
  if (resp) defaults.value = resp;
};

const openDefaultsManage = () => {
  if (visibleDefaults.value.length === 0) return;
  openDefaultEditor(visibleDefaults.value[0], 0);
};

function openDefaultEditor(item: SysConfigItem, idx: number) {
  defaultsEditingItem.value = { ...item };
  defaultsEditingOriginal.value = { ...item };
  // 货币参数存字符串，数值参数 parseFloat
  defaultEditValue.value = isCurrencyKey(item.configKey)
    ? item.configValue
    : parseFloat(item.configValue) || 0;
  defaultsCurrentIndex.value = idx;
  defaultsDrawerVisible.value = true;
}

function prevDefault() {
  if (defaultsCurrentIndex.value <= 0) return;
  openDefaultEditor(
    visibleDefaults.value[defaultsCurrentIndex.value - 1],
    defaultsCurrentIndex.value - 1
  );
}

function nextDefault() {
  if (defaultsCurrentIndex.value >= visibleDefaults.value.length - 1) return;
  openDefaultEditor(
    visibleDefaults.value[defaultsCurrentIndex.value + 1],
    defaultsCurrentIndex.value + 1
  );
}

async function confirmDefaultEdit() {
  if (!defaultsEditingItem.value || defaultValidationError.value !== "") return;
  // 货币参数直接提交字符串，数值参数保持原精度语义（String(number)）
  const newValStr = String(defaultEditValue.value);
  if (newValStr === defaultsEditingOriginal.value?.configValue) {
    ElMessage.info("新值与当前值相同");
    return;
  }
  defaultsSaving.value = true;
  try {
    const resp = await updateSysConfigApi(
      defaultsEditingItem.value.configKey,
      newValStr
    );
    if (resp !== null) {
      ElMessage.success(
        `默认参数「${defaultsEditingItem.value.cname}」已修改，仅影响之后新建的船舶`
      );
      defaultsDrawerVisible.value = false;
      await loadDefaults();
      await loadShipConfig();
    }
  } finally {
    defaultsSaving.value = false;
  }
}

/* 该船参数（svc_ship_config）：编辑区 */
const shipDefaults = ref<SysConfigItem[]>([]);
const shipsLoading = ref(false);
const shipOptions = ref<ShipConfigShip[]>([]);
const shipEditValues = ref<Record<string, number | string>>({});
const savingShip = ref(false);
const resetting = ref(false);

const currentShip = computed<ShipConfigShip | null>(
  () => shipOptions.value.find((s) => s.shipId === shipId.value) ?? null
);
const currentShipName = computed(
  () => currentShip.value?.shipName ?? "参数配置"
);
const customizedCount = computed(
  () => currentShip.value?.customizedKeys?.length ?? 0
);
function isCustomized(id: string): boolean {
  return !!currentShip.value?.customizedKeys?.includes(id);
}
function isEdited(id: string): boolean {
  const cur = shipEditValues.value[id];
  const ship = currentShip.value;
  const origRaw = ship?.values?.[id];
  // 货币/SIM 卡号参数：字符串比较
  if (isCurrencyKey(id) || isSimKey(id)) {
    if (isSimKey(id)) {
      return String(cur ?? "").trim() !== String(origRaw ?? "").trim();
    }
    return typeof cur === "string" && cur !== origRaw;
  }
  const orig = parseFloat(origRaw ?? "NaN");
  if (typeof cur !== "number" || Number.isNaN(orig)) return false;
  return Math.abs(cur - orig) > 1e-9;
}
const shipDirty = computed(() =>
  shipDefaults.value.some((d) => isEdited(d.configKey))
);

/**
 * 改币自动换算单价：货币下拉变化时，柴油/电价编辑框按 当前值 × 汇率(旧币→新币)
 * 即时换算（保留自定义定价水平），管理员可再手动微调后保存。
 *
 * 回填保护：页面加载/刷新时 fillShipEditValues 回填库值（如用户端改币后的新值）
 * 同样会触发值变化——但此时单价已是后端换算好的正确值，绝不能再乘一次汇率
 * （否则出现"第一次刷新显示二次换算错值、第二次才正常"）。
 * 用 suppress 标志抑制：watch 默认在 nextTick 前批量触发，标志清除放 nextTick 后。
 */
let suppressCurrencyWatch = false;
watch(
  () => shipEditValues.value[CURRENCY_KEY] as string | undefined,
  (val, old) => {
    if (suppressCurrencyWatch || !val || !old || val === old) return;
    const fx = fxOf(old, val);
    if (fx == null) {
      ElMessage.warning(
        `${old} → ${val} 的汇率未录入，请手动核对柴油/电价单价的数值`
      );
      return;
    }
    const round3 = (v: number) => Number(v.toFixed(3));
    const d = shipEditValues.value["calc.price.diesel"];
    const e = shipEditValues.value["calc.price.electricity"];
    if (typeof d === "number")
      shipEditValues.value["calc.price.diesel"] = round3(d * fx);
    if (typeof e === "number")
      shipEditValues.value["calc.price.electricity"] = round3(e * fx);
    ElMessage.info(
      `单价已按汇率 ${old} → ${val} 等价换算（可再手动调整后保存）`
    );
  }
);

const loadShipConfig = async () => {
  shipsLoading.value = true;
  try {
    const resp = await getShipConfigListApi();
    if (resp) {
      shipDefaults.value = resp.defaults;
      shipOptions.value = resp.ships;
      if (!currentShip.value) {
        ElMessage.warning("该船暂无参数记录");
      }
      fillShipEditValues();
    }
  } finally {
    shipsLoading.value = false;
  }
};

function fillShipEditValues() {
  const ship = currentShip.value;
  const values: Record<string, number | string> = {};
  for (const d of shipDefaults.value) {
    const raw = ship?.values?.[d.configKey] ?? d.configValue;
    // 货币/SIM 卡号参数存字符串，数值参数 parseFloat（异常回 0）
    if (isCurrencyKey(d.configKey) || isSimKey(d.configKey)) {
      values[d.configKey] = isCurrencyKey(d.configKey)
        ? raw || "CNY"
        : raw || "";
      continue;
    }
    const num = parseFloat(raw);
    values[d.configKey] = Number.isFinite(num) ? num : 0;
  }
  // 回填保护：抑制"改币自动换算 watch"（回填的是库值，单价已是正确值，
  // 触发换算会造成二次乘汇率 → 第一次刷新显示错值）
  suppressCurrencyWatch = true;
  shipEditValues.value = values;
  nextTick(() => {
    suppressCurrencyWatch = false;
  });
}

async function saveShip() {
  if (!shipId.value || !currentShip.value) return;
  const currencyBefore = String(
    currentShip.value.values?.[CURRENCY_KEY] ?? "CNY"
  );
  const currencyAfter = String(
    shipEditValues.value[CURRENCY_KEY] ?? currencyBefore
  );
  for (const d of shipDefaults.value) {
    // 货币走下拉、SIM 卡号为可空字符串，均无需正数校验
    if (isCurrencyKey(d.configKey) || isSimKey(d.configKey)) continue;
    const v = shipEditValues.value[d.configKey];
    if (typeof v !== "number" || !(v > 0) || !Number.isFinite(v)) {
      ElMessage.warning(`「${d.cname}」必须为正数`);
      return;
    }
  }
  // SIM 卡号格式校验（可空；非空时仅字母/数字/连字符）
  const simItem = shipDefaults.value.find((d) => isSimKey(d.configKey));
  if (simItem) {
    const sim = String(shipEditValues.value[simItem.configKey] ?? "").trim();
    if (sim && !/^[0-9A-Za-z-]{1,32}$/.test(sim)) {
      ElMessage.warning("SIM卡号仅支持 1~32 位字母/数字/连字符");
      return;
    }
  }
  // 只读参数不提交（后端同样拦截，此处避免整批 400）
  const items = shipDefaults.value
    .filter((d) => !isReadonlyKey(d.configKey))
    .map((d) => ({
      configKey: d.configKey,
      configValue: isCurrencyKey(d.configKey)
        ? String(shipEditValues.value[d.configKey])
        : isSimKey(d.configKey)
        ? String(shipEditValues.value[d.configKey] ?? "").trim()
        : String(
            Number.isFinite(shipEditValues.value[d.configKey])
              ? Number(
                  (shipEditValues.value[d.configKey] as number).toFixed(
                    precisionOf(d)
                  )
                )
              : shipEditValues.value[d.configKey]
          ),
    }));
  savingShip.value = true;
  try {
    const resp = await updateShipConfigApi(shipId.value, items);
    if (resp !== null) {
      // 币种变更提醒：单价数值不会自动换算，需人工按新币种语义核对
      if (currencyAfter !== currencyBefore) {
        ElMessage.warning(
          `本位货币已由 ${currencyBefore} 改为 ${currencyAfter}，请立即核对该船「柴油单价 / 平均电价」的数值（按 ${currencyAfter} 计价重新填写）`
        );
      }
      ElMessage.success(
        `船舶「${currentShip.value.shipName}」参数已保存，用户端 60 秒内生效`
      );
      await loadShipConfig();
    }
  } finally {
    savingShip.value = false;
  }
}

async function resetShip() {
  if (!shipId.value || !currentShip.value) return;
  resetting.value = true;
  try {
    const resp = await resetShipConfigApi(shipId.value);
    if (resp !== null) {
      ElMessage.success(
        `船舶「${currentShip.value.shipName}」参数已恢复默认值`
      );
      await loadShipConfig();
    }
  } finally {
    resetting.value = false;
  }
}

/** 单参数恢复默认（卡片右上角按钮；货币恢复联动单价，单价按该船币种换算默认） */
const keyResetting = ref<string>("");
const resetSingleKey = async (d: SysConfigItem) => {
  if (!shipId.value || keyResetting.value) return;
  const currencyNote = isSimKey(d.configKey)
    ? "（SIM 卡号默认为空，恢复即清空已录入的卡号）"
    : isCurrencyKey(d.configKey)
    ? "；柴油/电价单价将同时按默认货币联动恢复"
    : isPriceKey(d.configKey)
    ? "；将按本船币种取换算默认值"
    : "";
  try {
    await ElMessageBox.confirm(
      `确定将「${d.cname}」恢复为默认值${currencyNote}？`,
      "恢复默认",
      {
        confirmButtonText: "恢复",
        cancelButtonText: "取消",
        type: "warning",
      }
    );
  } catch {
    return;
  }
  keyResetting.value = d.configKey;
  try {
    const resp = await resetShipConfigKeyApi(shipId.value, d.configKey);
    if (resp !== null) {
      ElMessage.success(`「${d.cname}」已恢复默认值`);
      await loadShipConfig();
    }
  } finally {
    keyResetting.value = "";
  }
};

/* 加载 / 工具 / 路由返回 */
const reloadAll = async () => {
  loading.value = true;
  try {
    await Promise.all([loadDefaults(), loadShipConfig()]);
  } finally {
    loading.value = false;
  }
};

const goBack = () => {
  router.push({ name: "shipList" });
};

const formattedNow = computed(() => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
});

function stepOf(item: SysConfigItem): number {
  if (item.configKey.includes("price")) return 0.001;
  if (item.configKey.includes("carbon")) return 0.01;
  return 0.1;
}
function precisionOf(item: SysConfigItem): number {
  // 单价（柴油/电价）统一 3 位小数（外币换算值需要更高精度）；碳因子 3 位
  if (item.configKey.includes("price")) return 3;
  if (item.configKey.includes("carbon")) return 3;
  return 2;
}

function relativeTime(t: string): string {
  if (!t) return "--";
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return t;
  const diff = Date.now() - d.getTime();
  if (diff < 0) return formatFullTime(t);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec} 秒前`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} 分钟前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小时前`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} 天前`;
  return formatFullTime(t);
}

function formatFullTime(t: string): string {
  if (!t) return "--";
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return t;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

onMounted(() => {
  loaded.value = true;
  // 币种下拉数据（已录汇率的币种 + CNY）+ 当前生效汇率（外币船默认值换算显示），并行加载
  loadCurrencyOptions().catch(() => void 0);
  loadEffectiveRates().catch(() => void 0);
  if (shipId.value) {
    reloadAll();
  }
});
onUnmounted(() => {
  /* noop */
});
</script>

<style lang="less" scoped>
.ship-cp {
  --p-cyan: #06b6d4;
  --p-cyan-deep: #0891b2;
  --p-cyan-soft: #ecfeff;
  --p-emerald: #10b981;
  --p-emerald-deep: #059669;
  --p-emerald-soft: #ecfdf5;
  --p-amber: #f59e0b;
  --p-amber-deep: #d97706;
  --p-amber-soft: #fffbeb;
  --p-slate-50: #f8fafc;
  --p-slate-100: #f1f5f9;
  --p-slate-200: #e2e8f0;
  --p-slate-300: #cbd5e1;
  --p-slate-400: #94a3b8;
  --p-slate-500: #64748b;
  --p-slate-600: #475569;
  --p-slate-700: #334155;
  --p-slate-800: #1e293b;

  display: flex;
  flex-direction: column;
  gap: 18px;
  height: 100%;
  padding: 18px 20px 24px;
  overflow: auto;
  font-family: "Microsoft Yahei", system-ui, sans-serif;
}

.ship-cp__hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  background: linear-gradient(135deg, #ffffff 0%, #f0f9ff 50%, #ecfeff 100%);
  border: 1px solid rgba(8, 145, 178, 0.12);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04),
    0 8px 24px rgba(8, 145, 178, 0.06);
}
.hero__left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.hero__back {
  margin-right: 4px;
  background: rgba(8, 145, 178, 0.06);
  color: var(--p-cyan-deep);
}
.hero__icon {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    var(--p-cyan) 0%,
    var(--p-cyan-deep) 100%
  );
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(8, 145, 178, 0.3);
}
.hero__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--p-slate-800);
  letter-spacing: 0.4px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.hero__title-divider {
  color: var(--p-slate-300);
  font-weight: 400;
}
.hero__title-sub {
  font-size: 16px;
  color: var(--p-cyan-deep);
  font-weight: 600;
}
.hero__subtitle {
  margin: 2px 0 0;
  font-size: 12px;
  color: var(--p-slate-500);
}
.hero__time {
  color: var(--p-cyan-deep);
  font-weight: 600;
  font-family: "JetBrains Mono", "Cascadia Mono", monospace;
  margin-left: 2px;
}
.hero__actions {
  display: flex;
  gap: 10px;
}

.ship-cp__empty {
  padding: 80px 0;
}

.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid var(--p-slate-200);
  border-radius: 12px 12px 0 0;
  border-bottom: none;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

  &__title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 700;
    color: var(--p-slate-800);
    .el-icon {
      color: var(--p-cyan-deep);
    }
  }
  &__tag {
    margin-left: 4px;
  }
  &__actions {
    display: inline-flex;
    align-items: center;
    gap: 10px;
  }
}

.ship-cp__defaults {
  display: flex;
  flex-direction: column;
}

.defaults-table {
  background: #ffffff;
  border: 1px solid var(--p-slate-200);
  border-radius: 0 0 12px 12px;
  overflow: hidden;

  &__head,
  &__row {
    display: grid;
    grid-template-columns: 1.6fr 1fr 0.8fr;
    align-items: center;
    padding: 10px 16px;
    gap: 12px;
  }
  &__head {
    background: var(--p-slate-50);
    border-bottom: 1px solid var(--p-slate-200);
    font-size: 11px;
    font-weight: 600;
    color: var(--p-slate-500);
    letter-spacing: 0.5px;
  }
  &__row {
    border-bottom: 1px dashed var(--p-slate-100);
    font-size: 13px;
    color: var(--p-slate-700);
    transition: background 0.2s ease;
    &:last-of-type {
      border-bottom: none;
    }
    &:hover {
      background: rgba(8, 145, 178, 0.03);
    }
    &.is-carbon .defaults-table__chip {
      background: var(--p-emerald-soft);
      color: var(--p-emerald-deep);
    }
    &.is-price .defaults-table__chip {
      background: var(--p-amber-soft);
      color: var(--p-amber-deep);
    }
    &.is-other .defaults-table__chip {
      background: var(--p-slate-100);
      color: var(--p-slate-600);
    }
  }
  &__name {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--p-slate-800);
    font-weight: 600;
  }
  &__chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 6px;
  }
  &__value {
    font-family: "JetBrains Mono", monospace;
    font-weight: 700;
    font-size: 16px;
    color: var(--p-cyan-deep);
  }
  &__unit {
    font-family: "JetBrains Mono", monospace;
    font-size: 11px;
    color: var(--p-slate-400);
  }
  &__foot {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 16px;
    background: linear-gradient(180deg, var(--p-amber-soft) 0%, #ffffff 100%);
    border-top: 1px dashed rgba(217, 119, 6, 0.18);
    font-size: 12px;
    color: var(--p-amber-deep);
    .el-icon {
      color: var(--p-amber);
    }
  }
}

.section-empty {
  background: #ffffff;
  border: 1px solid var(--p-slate-200);
  border-radius: 0 0 12px 12px;
}

.section-skeleton {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 14px;
  padding: 14px;
  background: #ffffff;
  border: 1px solid var(--p-slate-200);
  border-radius: 0 0 12px 12px;
  .skeleton-card {
    padding: 18px 20px;
  }
}

.ship-cp__ship {
  display: flex;
  flex-direction: column;
}
.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 4px;
  padding: 3px 10px;
  background: var(--p-cyan-soft);
  color: var(--p-cyan-deep);
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
}
.status-dot {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: var(--p-cyan);
  margin: 0 2px;
}
.ship-switch {
  flex: 0 0 220px;
}

.ship-param-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 14px;
  padding: 14px;
  background: #ffffff;
  border: 1px solid var(--p-slate-200);
  border-radius: 0 0 12px 12px;
}

.ship-param-card {
  position: relative;
  display: flex;
  background: #ffffff;
  border: 1px solid var(--p-slate-200);
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04),
    0 4px 14px rgba(15, 23, 42, 0.04);
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease,
    border-color 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(15, 23, 42, 0.06),
      0 12px 24px rgba(15, 23, 42, 0.06);
  }
  &__bar {
    flex-shrink: 0;
    width: 4px;
    background: var(--p-slate-300);
  }
  &--carbon &__bar {
    background: linear-gradient(
      180deg,
      var(--p-emerald) 0%,
      var(--p-emerald-deep) 100%
    );
  }
  &--price &__bar {
    background: linear-gradient(
      180deg,
      var(--p-amber) 0%,
      var(--p-amber-deep) 100%
    );
  }
  &--other &__bar {
    background: linear-gradient(
      180deg,
      var(--p-slate-400) 0%,
      var(--p-slate-600) 100%
    );
  }
  &.is-modified {
    border-color: rgba(8, 145, 178, 0.35);
    box-shadow: 0 0 0 2px rgba(8, 145, 178, 0.08),
      0 4px 14px rgba(15, 23, 42, 0.04);
  }
  &__body {
    flex: 1;
    min-width: 0;
    padding: 16px 18px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  &__head-right {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__reset {
    border: 1px solid var(--p-slate-200, #e2e8f0);
    background: #fff;
    border-radius: 6px;
    padding: 2px 8px;
    font-size: 11px;
    color: var(--p-slate-500, #64748b);
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover:not(:disabled) {
      color: var(--p-cyan-deep, #0e7490);
      border-color: var(--p-cyan-deep, #0e7490);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
  &__title {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: var(--p-slate-800);
    // 长名称在头部 flex 布局中可换行（不截断）
    flex: 1;
    min-width: 0;
    word-break: break-word;
  }
  &__tag {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 600;
    background: var(--p-cyan-soft);
    color: var(--p-cyan-deep);
    white-space: nowrap;
    &--editing {
      background: var(--p-amber-soft);
      color: var(--p-amber-deep);
    }
    &--readonly {
      background: var(--p-slate-100, #f1f5f9);
      color: var(--p-slate-500, #64748b);
      cursor: help;
    }
  }
  &__desc {
    margin: 0;
    font-size: 12px;
    color: var(--p-slate-500);
    line-height: 1.6;
    // 完整展示说明文字（不截断，长文本自然换行）
    word-break: break-word;
    overflow-wrap: anywhere;
  }
  &__footer {
    padding-top: 8px;
    border-top: 1px dashed var(--p-slate-200);
  }
  &__key {
    font-family: "JetBrains Mono", monospace;
    font-size: 10px;
    padding: 1px 6px;
    background: var(--p-slate-50);
    color: var(--p-slate-500);
    border-radius: 4px;
    letter-spacing: 0.2px;
  }
}

.ship-param-card__compare {
  display: flex;
  align-items: stretch;
  gap: 8px;
}
.ship-compare-col {
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--p-slate-50);
  border: 1px solid var(--p-slate-200);
  text-align: center;

  &--now {
    background: #ffffff;
    border-color: var(--p-cyan);
    box-shadow: 0 0 0 2px rgba(8, 145, 178, 0.08);
  }
  &__label {
    font-size: 11px;
    color: var(--p-slate-400);
    font-weight: 600;
    letter-spacing: 0.4px;
  }
  &__value {
    margin-top: 4px;
    font-size: 22px;
    font-weight: 700;
    font-family: "JetBrains Mono", monospace;
    color: var(--p-slate-600);
    line-height: 1.1;
  }
  &__unit {
    margin-top: 4px;
    font-size: 10px;
    color: var(--p-slate-400);
    font-family: "JetBrains Mono", monospace;
  }
}

.ship-compare-input {
  width: 100%;
  margin-top: 2px;
  :deep(.el-input__inner) {
    font-size: 20px;
    font-family: "JetBrains Mono", monospace;
    font-weight: 700;
    color: var(--p-cyan-deep);
    text-align: center;
  }
}

/* SIM 卡号：长字符串（最长 32 位）缩小字号，保持等宽居中 */
.ship-compare-input--sim {
  :deep(.el-input__inner) {
    font-size: 15px;
    letter-spacing: 0.5px;
  }
}

.ship-compare-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--p-slate-300);
}

/* Drawer */
::deep(.defaults-drawer) {
  .el-drawer__body {
    padding: 0;
    background: linear-gradient(180deg, #ffffff 0%, var(--p-slate-50) 100%);
  }
  .el-drawer__header {
    margin: 0;
    padding: 0;
  }
  .el-drawer__footer {
    padding: 0;
    border: none;
  }
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 24px;
  background: linear-gradient(135deg, #ffffff 0%, var(--p-cyan-soft) 100%);
  border-bottom: 1px solid var(--p-slate-200);

  &__title {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    h2 {
      margin: 0;
      font-size: 17px;
      font-weight: 700;
      color: var(--p-slate-800);
      line-height: 1.3;
    }
  }
  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
    &.is-carbon {
      background: var(--p-emerald-soft);
      color: var(--p-emerald-deep);
    }
    &.is-price {
      background: var(--p-amber-soft);
      color: var(--p-amber-deep);
    }
    &.is-other {
      background: var(--p-slate-100);
      color: var(--p-slate-600);
    }
  }
}

.drawer-body {
  padding: 18px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.drawer-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  background: #ffffff;
  border: 1px solid var(--p-slate-200);
  border-radius: 12px;
  &__label {
    font-size: 12px;
    color: var(--p-slate-500);
    font-weight: 600;
    letter-spacing: 0.3px;
  }
}

.drawer-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 24px;
  background: var(--p-slate-50);
  border-top: 1px solid var(--p-slate-200);
}

.meta-row {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  min-height: 22px;

  &__label {
    flex-shrink: 0;
    width: 70px;
    color: var(--p-slate-400);
    font-weight: 500;
  }
  &__value {
    flex: 1;
    min-width: 0;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--p-slate-700);
    font-size: 12px;
  }
  &__sep {
    margin: 0 4px;
    color: var(--p-slate-300);
  }
}

code.meta-row__value {
  font-family: "JetBrains Mono", monospace;
  background: var(--p-slate-50);
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  color: var(--p-cyan-deep);
  border: 1px solid var(--p-slate-200);
}

.drawer-input {
  width: 100%;
  :deep(.el-input-number__increase),
  :deep(.el-input-number__decrease) {
    width: 36px;
    background: var(--p-slate-50);
  }
  :deep(.el-input__inner) {
    font-size: 18px;
    font-family: "JetBrains Mono", monospace;
    font-weight: 600;
    color: var(--p-cyan-deep);
  }
}

.drawer-error {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  background: #fff1f2;
  color: var(--p-slate-800);
  border-radius: 6px;
  font-size: 12px;
}

.drawer-nav {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  &__pos {
    font-size: 12px;
    font-weight: 600;
    color: var(--p-slate-500);
    font-family: "JetBrains Mono", monospace;
  }
  &__divider {
    margin: 0 4px;
    color: var(--p-slate-300);
  }
}
</style>
